import { observer } from "mobx-react"; // Import MobX observer for state management
import moment from "moment"; // Import Moment.js for date formatting
import React from "react"; // Import React library
import { Modal } from "react-bootstrap"; // Import Bootstrap modal component
import { FormattedMessage } from "react-intl"; // Import internationalization for messages
import { getEventWithMostOverlaps } from "../../../../constants/constants"; // Import function to get events with most overlaps
import { formatMessage } from "../../../../translations/format-message"; // Import function for message formatting
import AddBreakDetailsPopupHOC from "../hoc/add-break-details-popup-hoc"; // Import higher-order component for add break details popup
import AllMoreEventsPopupHOC from "../hoc/all-more-events-popup-hoc"; // Import higher-order component for all more events popup
import ReservationDetailsPopupHOC from "../hoc/reservation-details-popup-hoc"; // Import higher-order component for reservation details popup
import ServiceNameDisplayComponent from "../reservation-components/services-name-component"; // Import component to display service names
import ReservationDetailsComponent from "./reservation-details-component"; // Import reservation details component
import AddBreakDetailsComponent from "./add-break-details-component"; // Import add break details component

// Define interface for component props
interface IProps {
  onModalClosed: () => void; // Callback for closing the modal
  matchedEvent: any; // Matched event data
  AllEvents: any[]; // List of all events
  showReservationDetailsPopup: boolean; // Flag for showing reservation details popup
  addBreakDetailsPopup?: boolean; // Flag for showing add break details popup (optional)
  viewOnly: boolean; // Flag for view-only mode
  storeId: number; // Store ID
  day: any; // View type (e.g., daily, weekly)
  changeView?: (date: string) => void; // Optional function to change view
  breaks?: any[]; // List of all breaks (optional)
  allBreaks?: any[]; // List of all breaks (optional)
  staffList?: any[]; // List of staff options (optional)
}

// Define functional component
const AllMoreEventsComponents = (props: any) => {
  const [selectedEvent, setSelectedEvent] = React.useState<any>(); // State for selected event
  const [selectedBreak, setSelectedBreak] = React.useState<any>(); // State for selected break

  // Handler to open reservation details
  const openReservationToggleHandler = (event: any) => {
    setSelectedEvent(event); // Set selected event
    props.reservationDetailsToggleHandler(); // Call reservation details toggle handler
  };

  // Handler to open add break details popup
  const openAddBreakDetailsPopupToggleHandler = (event: any) => {
    setSelectedBreak(event); // Set selected break
    props.addBreakDetailsToggleHandler(); // Call add break details toggle handler
  };

  return (
    <React.Fragment>
      <Modal show={true} centered size="sm">
        {" "}
        {/* Render modal */}
        {props.showReservationDetailsPopup && ( // Check if reservation details popup should be shown
          <ReservationDetailsComponent
            onModalClosed={props.reservationDetailsToggleHandler} // Pass close handler
            event={selectedEvent} // Pass selected event data
            storeId={props.storeId} // Pass store ID
            viewOnly={props.viewOnly} // Pass view-only flag
          />
        )}
        {props.addBreakDetailsPopup && ( // Check if add break details popup should be shown
          <AddBreakDetailsComponent
            id={selectedBreak?.BreakID} // Pass BreakID to AddBreakDetailsComponent
            staffId={selectedBreak?.StaffId} // Pass staffId to AddBreakDetailsComponent
            staffList={props.staffList} // Pass staffList to AddBreakDetailsComponent
            startTime={selectedBreak?.StartTime} // Pass startTime to AddBreakDetailsComponent
            onModalClosed={props.addBreakDetailsToggleHandler} // Pass toggle handler to AddBreakDetailsComponent
            storeId={props.storeId} // Pass store ID
            viewOnly={props.viewOnly} // Pass view-only flag
          />
        )}
        {/* Modal configuration */}
        <Modal.Header>
          {" "}
          {/* Modal header */}
          <Modal.Title>
            {<FormattedMessage id="all_more_events" />}{" "}
            {/* Modal title with internationalization */}
            <button
              onClick={() => {
                props.onModalClosed(); // Close modal on button click
              }}
              className="Crossicons" // Class for close icon
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="23"
                height="23"
                fill="currentColor"
                className="bi bi-x" // Class for SVG icon
                viewBox="0 0 16 16"
              >
                <path d="M4.646 4.646a.5.5 0 0 1 .708 0L8 7.293l2.646-2.647a.5.5 0 0 1 .708.708L8.707 8l2.647 2.646a.5.5 0 0 1-.708.708L8 8.707l-2.646 2.647a.5.5 0 0 1-.708-.708L7.293 8 4.646 5.354a.5.5 0 0 1 0-.708z" />{" "}
                {/* Close icon path */}
              </svg>
            </button>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {" "}
          {/* Modal body */}
          <div className="all-more-events">
            {" "}
            {/* Container for event details */}
            <div className="row px-4 mb-2">
              {" "}
              {/* Row for date display */}
              <div className="col-6">{formatMessage("date")} :</div>{" "}
              {/* Label for date */}
              <div className="col-6">
                {moment(props.matchedEvent?.Date)?.format("YYYY-MM-DD")}{" "}
                {/* Formatted event date */}
              </div>
            </div>
            <div className="row px-4 align-items-center mb-2">
              {" "}
              {/* Row for all events display */}
              <div className="col-6 mb-2">
                {formatMessage("all_events")}:
              </div>{" "}
              {/* Label for all events */}
              <div className="col-6 text-end mb-2">
                {" "}
                {/* Button to view full day */}
                {props.day.props["data-role"] == "weekly" && ( // Check if view is weekly
                  <button
                    type="button"
                    className="btn-link"
                    onClick={() =>
                      props.changeView && // Check if changeView function exists
                      props.changeView(
                        moment(props.matchedEvent?.Date)?.format("YYYY-MM-DD") // Call changeView with formatted date
                      )
                    }
                  >
                    {formatMessage("view_full_day")} {/* Button text */}
                  </button>
                )}
              </div>
              {/* {JSON.stringify(props.breaks)} */}{" "}
              {/* Debugging line for breaks */}
              {/* Label for all events */}
              {getEventWithMostOverlaps(props.matchedEvent, props.AllEvents) && // Check for events with overlaps
                getEventWithMostOverlaps(
                  props.matchedEvent,
                  props.AllEvents
                )?.OverlappedEventsIds?.map((eventId: any, index: number) => {
                  // Map through overlapped event IDs
                  const event = props.AllEvents?.find(
                    // Find event by ID
                    (event: any) => event.Id === eventId
                  );
                  return (
                    <div key={index} className="event-box">
                      {" "}
                      {/* Event box */}
                      <div
                        className="row px-2"
                        onClick={() => {
                          openReservationToggleHandler(event); // Call function to open reservation details
                        }}
                      >
                        <div
                          className="event mb-2" // Event display styling
                          style={{
                            cursor: "pointer", // Pointer cursor for clickable area
                            width: "100%", // Full width
                            borderRadius: "4px", // Rounded corners
                          }}
                        >
                          <span>{event?.CustomerName}</span>{" "}
                          {/* Display customer name */}
                          <br />
                          <span>
                            {event?.StartTime} ~ {event?.EndTime}{" "}
                            {/* Display event time range */}
                          </span>
                          <div className="treatment-name">
                            {" "}
                            {/* Treatment name or additional details */}
                            <ServiceNameDisplayComponent
                              services={event?.ServiceRequestedList} // List of requested services
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              {props.breaks &&
                props.breaks.length > 0 && ( // Check if there are breaks to display
                  <div className="breaks-list">
                    {" "}
                    {/* Container for breaks list */}
                    {props.breaks.map(
                      (
                        breakItem: any,
                        index: number // Map through breaks
                      ) => (
                        <div key={index} className="event-box">
                          {" "}
                          {/* Event box for break */}
                          <div
                            className="row px-2"
                            onClick={() => {
                              openAddBreakDetailsPopupToggleHandler(breakItem); // Call function to open add break details
                            }}
                          >
                            <div
                              className="event break mb-2" // Break display styling
                              style={{
                                cursor: "pointer", // Pointer cursor for clickable area
                                width: "100%", // Full width
                                borderRadius: "4px", // Rounded corners
                              }}
                            >
                              <span>{breakItem?.Title}</span>{" "}
                              {/* Display break title */}
                              <br />
                              <span>
                                {breakItem?.StartTime} ~ {breakItem?.EndTime}{" "}
                                {/* Display break time range */}
                              </span>
                            </div>
                          </div>
                        </div>
                      )
                    )}
                  </div>
                )}
            </div>
          </div>
        </Modal.Body>
      </Modal>
    </React.Fragment>
  );
};

// Export wrapped component with higher-order components
export default AddBreakDetailsPopupHOC(
  AllMoreEventsPopupHOC(
    ReservationDetailsPopupHOC(observer(AllMoreEventsComponents))
  )
);
